import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Zap } from "lucide-react";

// Featured Listing plans → Stripe subscription lookup keys
const LOOKUP_BY_TIER = {
  basic:   "featured_listing_7d",
  pro:     "featured_listing_30d",
  premium: "featured_listing_90d",
};

export default function Upgrade() {
  const { listingId } = useParams();
  const nav = useNavigate();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(null);
  const [listing, setListing] = useState(null);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setPlans(data.plans));
    api.get(`/listings/${listingId}`).then(({ data }) => setListing(data));
  }, [listingId]);

  const start = async (tier) => {
    setLoading(tier);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        lookup_key: LOOKUP_BY_TIER[tier],
        target_id: listingId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setLoading(null);
    }
  };

  const perks = {
    basic: ["Featured badge on card", "Pinned above regular listings", "Renews every 7 days"],
    pro: ["Featured badge", "Top of its category", "Renews every 30 days", "Priority in search"],
    premium: ["Featured everywhere", "Homepage highlight", "Renews every 3 months", "Priority support"],
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14">
      <div className="overline text-safety">Boost your reach</div>
      <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tighter text-forest">
        Feature your listing
      </h1>
      {listing && (
        <p className="mt-3 text-muted-foreground">
          For: <span className="font-medium text-foreground">{listing.title}</span>
        </p>
      )}

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(plans).map(([id, plan], idx) => (
          <div
            key={id}
            data-testid={`upgrade-plan-${id}`}
            className={`border bg-white p-8 flex flex-col ${idx === 1 ? "border-forest md:-translate-y-2 relative" : "border-border"}`}
          >
            {idx === 1 && (
              <div className="absolute -top-3 left-8 bg-safety text-white overline px-3 py-1 text-[10px]">Most popular</div>
            )}
            <div className="overline text-safety">{id}</div>
            <div className="mt-2 font-display font-black text-4xl text-forest tabular-nums">£{plan.amount}</div>
            <div className="mt-1 text-sm text-muted-foreground">{plan.label}</div>
            <ul className="mt-6 space-y-3 text-sm flex-1">
              {perks[id].map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-forest mt-0.5" /> {p}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => start(id)}
              disabled={loading === id}
              data-testid={`upgrade-buy-${id}`}
              className={`mt-8 rounded-none h-12 ${idx === 1 ? "bg-safety hover:bg-safety-hover text-white" : "bg-forest hover:bg-forest-800 text-white"}`}
            >
              {loading === id ? "Redirecting…" : (<><Zap className="w-4 h-4 mr-2" /> Upgrade to {id}</>)}
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-8 text-xs text-muted-foreground">
        Secure payments powered by Stripe. Card details never touch our servers.
      </div>
    </div>
  );
}
